import { useEffect, useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModuleHeader } from '@/components/ui/module-header';
import { DataTable } from '@/components/ui/data-table';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StatusPill } from '@/components/ui/status-pill';
import { FloatingField } from '@/components/ui/floating-field';
import { Checkbox } from '@/components/ui/checkbox';
import { formatCurrency } from '@/lib/utils';
import * as Icons from 'lucide-react';
import { toast } from 'sonner';
import { api, getApiBase, getToken } from '@/lib/api';

export default function FactoringPage() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    factor: 'Primary Factor',
    advance: '90',
    reserve: '10',
    fee: '3',
    selectedInvoices: [],
  });

  const dataQ = useQuery({
    queryKey: ['factoring-page'],
    queryFn: async () => {
      const [inv, sch] = await Promise.all([
        api('/api/v1/invoices'),
        api('/api/v1/factor-schedules'),
      ]);
      if (inv.status !== 200) throw new Error(inv.data?.error ?? 'Failed to load invoices');
      if (sch.status !== 200) throw new Error(sch.data?.error ?? 'Failed to load schedules');
      return {
        invoices: (inv.data.invoices ?? []).filter(
          (i) =>
            !i.factorScheduleId &&
            (i.status === 'sent' || i.status === 'partial') &&
            i.amountCents - (i.amountPaidCents ?? 0) > 0
        ),
        schedules: sch.data.schedules ?? [],
      };
    },
  });

  useEffect(() => {
    if (dataQ.isError) toast.error(dataQ.error?.message ?? 'Failed to load factoring');
  }, [dataQ.isError, dataQ.error]);

  const invoices = dataQ.data?.invoices ?? [];
  const schedules = dataQ.data?.schedules ?? [];

  const toggleInvoice = (id) => {
    setForm({
      ...form,
      selectedInvoices: form.selectedInvoices.includes(id)
        ? form.selectedInvoices.filter((i) => i !== id)
        : [...form.selectedInvoices, id],
    });
  };

  const totalSelected = useMemo(
    () =>
      invoices
        .filter((i) => form.selectedInvoices.includes(i.id))
        .reduce((s, i) => s + (i.amountCents - (i.amountPaidCents ?? 0)) / 100, 0),
    [invoices, form.selectedInvoices]
  );

  const handleCreate = async () => {
    if (!form.factor || form.selectedInvoices.length === 0) {
      toast.error('Factor name and at least 1 invoice required');
      return;
    }
    const r = await api('/api/v1/factor-schedules', {
      method: 'POST',
      body: {
        invoiceIds: form.selectedInvoices,
        factorName: form.factor,
        advancePct: Number(form.advance) || 0,
        reservePct: Number(form.reserve) || 0,
        feePct: Number(form.fee) || 0,
      },
    });
    if (r.status === 201) {
      toast.success('Factor schedule created');
      setForm({
        factor: 'Primary Factor',
        advance: '90',
        reserve: '10',
        fee: '3',
        selectedInvoices: [],
      });
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['factoring-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const act = async (id, action) => {
    const r = await api(`/api/v1/factor-schedules/${id}/${action}`, {
      method: 'POST',
      body: {},
    });
    if (r.status === 200) {
      toast.success(action === 'submit' ? 'Submitted' : 'Marked funded');
      qc.invalidateQueries({ queryKey: ['factoring-page'] });
    } else {
      toast.error(String(r.data?.error ?? 'Failed'));
    }
  };

  const exportCsv = (id) => {
    const token = getToken();
    const url = `${getApiBase()}/api/v1/factor-schedules/${id}/export.csv`;
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    })
      .then(async (res) => {
        if (!res.ok) throw new Error('Export failed');
        const blob = await res.blob();
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `factor-${id.slice(0, 8)}.csv`;
        a.click();
      })
      .catch(() => toast.error('Export failed'));
  };

  const columns = [
    { key: 'scheduleNumber', label: 'ID', className: 'font-mono' },
    { key: 'factorName', label: 'Factor', sortable: true },
    {
      key: 'totalAmountCents',
      label: 'Face',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'totalAdvanceCents',
      label: 'Advance',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'totalReserveCents',
      label: 'Reserve',
      render: (v) => <span className="font-mono">{formatCurrency((v || 0) / 100)}</span>,
    },
    {
      key: 'totalFeeCents',
      label: 'Fee',
      render: (v) => (
        <span className="font-mono text-destructive">{formatCurrency((v || 0) / 100)}</span>
      ),
    },
    { key: 'status', label: 'Status', render: (v) => <StatusPill status={v} /> },
    {
      key: 'actions',
      label: '',
      render: (_, row) => (
        <div className="flex gap-1">
          {row.status === 'draft' && (
            <Button size="sm" variant="outline" onClick={() => act(row.id, 'submit')}>
              Submit
            </Button>
          )}
          {(row.status === 'submitted' || row.status === 'draft') && (
            <Button size="sm" variant="ghost" onClick={() => act(row.id, 'fund')}>
              Funded
            </Button>
          )}
          <Button size="sm" variant="ghost" onClick={() => exportCsv(row.id)}>
            <Icons.Download className="h-3 w-3" />
          </Button>
        </div>
      ),
    },
  ];

  return (
<div className="space-y-6 motion-enter">
        <ModuleHeader
          title="Factoring"
          description="Manage invoice factoring schedules and advances"
          icon="Building2"
          metrics={[
            { label: 'Schedules', value: schedules.length },
            { label: 'Eligible', value: invoices.length },
          ]}
          actions={
            <Button onClick={() => setShowCreate(!showCreate)}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Create Schedule
            </Button>
          }
        />

        {showCreate && (
          <Card>
            <CardHeader>
              <CardTitle>New Factor Schedule</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="md:col-span-4">
                  <FloatingField
                    label="Factor Company Name"
                    value={form.factor}
                    onChange={(e) => setForm({ ...form, factor: e.target.value })}
                    required
                  />
                </div>
                <FloatingField
                  label="Advance %"
                  type="number"
                  step="0.1"
                  value={form.advance}
                  onChange={(e) => setForm({ ...form, advance: e.target.value })}
                  placeholder="90"
                />
                <FloatingField
                  label="Reserve %"
                  type="number"
                  step="0.1"
                  value={form.reserve}
                  onChange={(e) => setForm({ ...form, reserve: e.target.value })}
                  placeholder="10"
                />
                <FloatingField
                  label="Fee %"
                  type="number"
                  step="0.1"
                  value={form.fee}
                  onChange={(e) => setForm({ ...form, fee: e.target.value })}
                  placeholder="3"
                />
              </div>

              <div>
                <h4 className="font-semibold text-sm mb-2">Select Invoices</h4>
                <div className="border rounded-lg divide-y max-h-72 overflow-auto">
                  {invoices.length === 0 ? (
                    <p className="p-3 text-sm text-muted-foreground">No eligible invoices</p>
                  ) : (
                    invoices.map((inv) => (
                      <label
                        key={inv.id}
                        className="flex items-center gap-3 p-3 hover:bg-muted/30 cursor-pointer"
                      >
                        <Checkbox
                          checked={form.selectedInvoices.includes(inv.id)}
                          onCheckedChange={() => toggleInvoice(inv.id)}
                        />
                        <div className="flex-1 flex justify-between items-center">
                          <div>
                            <p className="font-mono text-sm">{inv.invoiceNumber}</p>
                            <p className="text-xs text-muted-foreground">{inv.billToName}</p>
                          </div>
                          <p className="font-mono font-medium">
                            {formatCurrency(
                              (inv.amountCents - (inv.amountPaidCents ?? 0)) / 100
                            )}
                          </p>
                        </div>
                      </label>
                    ))
                  )}
                </div>
                {form.selectedInvoices.length > 0 && (
                  <p className="text-sm mt-2">
                    Selected:{' '}
                    <span className="font-mono font-bold">{formatCurrency(totalSelected)}</span> (
                    {form.selectedInvoices.length} invoices)
                  </p>
                )}
              </div>

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setShowCreate(false)}>
                  Cancel
                </Button>
                <Button onClick={handleCreate}>Create Schedule</Button>
              </div>
            </CardContent>
          </Card>
        )}

        <DataTable
          columns={columns}
          data={schedules}
          emptyMessage={dataQ.isLoading ? 'Loading…' : 'No factor schedules'}
        />
      </div>
);
}
